import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Phone, Clock, Video, Tv, Camera, Wifi, Building2, Check, ArrowRight } from "lucide-react";
import { PrepChecklist } from "@/components/PrepChecklist";
import { InstallBanner } from "@/components/home/InstallBanner";

const TIERS = [
  { id: "30-min", icon: Phone, title: "30-Minute Session", price: "99.99", tagline: "Phone or video — one focused problem.",
    bullets: ["Remote, input or sound issues", "Wi-Fi drops, printer offline, login trouble", "Quick phone, tablet or smart-home question"] },
  { id: "1-hour", icon: Clock, title: "1-Hour Session", price: "149.99", tagline: "Phone or video — time to dig in.",
    bullets: ["Receiver, soundbar or streaming setup", "Camera app, alerts & recording tuning", "Mesh Wi-Fi, guest networks, multiple devices"] },
  { id: "90-min", icon: Video, title: "1½-Hour Video Call", price: "249.99", tagline: "Full video walkthrough for bigger jobs.",
    bullets: ["Whole home theater configuration", "Multi-camera security systems", "Small-business A/V, camera & network planning"] },
];

const AREAS = [
  { icon: Tv, title: "A/V & Home Theater", desc: "Receivers, soundbars, HDMI/ARC/4K sync, streaming boxes, universal remotes, buying advice." },
  { icon: Camera, title: "CCTV & Security Cameras", desc: "Reolink, Ubiquiti, Amcrest, Ring, Wyze — app setup, motion zones, storage, remote viewing." },
  { icon: Wifi, title: "Wi-Fi & Networking", desc: "Routers, mesh, extenders, dead zones, speed issues, VPN, port forwarding, IoT devices." },
  { icon: Building2, title: "Small Business", desc: "System design, parts lists, vendor coordination, meeting-room A/V, staff walkthroughs by video." },
];

export default function Services() {
  const [images, setImages] = useState({});
  useEffect(() => { api.get("/branding").then((r) => setImages(r.data.images || {})).catch(() => {}); }, []);
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <div className="mb-10 dg-reveal">
        <span className="dg-eyebrow">Services & pricing</span>
        <h1 className="mt-2 text-3xl sm:text-5xl font-extrabold tracking-tight font-display">Tech help by phone, video — or in person.</h1>
        <p className="mt-3 text-slate-300 max-w-3xl">Remote sessions are a phone call or a video call on Zoom, FaceTime, Google Meet or the free Meeting app — pick a length, pick an open time on David's calendar — you pay by secure card link after the session. Need something mounted, wired or installed? Scroll down for in-person installs in the LA area.</p>
        <p className="mt-2 text-sm text-slate-500 max-w-3xl" data-testid="services-onsite-note">And if a remote session can't fully fix it, David can send a technician to finish the job on-site for a separate service charge — always quoted before anyone is dispatched.</p>
      </div>

      <div className="grid gap-5 sm:grid-cols-2 md:grid-cols-3 mb-16">
        {TIERS.map((t, i) => {
          const Icon = t.icon;
          return (
            <div key={t.id} className={`dg-card rounded-2xl overflow-hidden flex flex-col dg-reveal dg-reveal-${i + 1}`} data-testid={`service-detail-${t.id}`}>
              {images[t.id] && <img src={`${process.env.REACT_APP_BACKEND_URL}${images[t.id]}`} alt={t.title} className="w-full h-40 object-cover" data-testid={`tier-img-${t.id}`} />}
              <div className="p-6 flex flex-col flex-1">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/30 grid place-items-center shrink-0">
                  <Icon className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <div className="font-display text-xl font-semibold">{t.title}</div>
                  <div className="text-2xl font-extrabold text-cyan-400 font-display">${t.price}</div>
                </div>
              </div>
              <p className="text-slate-400 mt-3 text-sm">{t.tagline}</p>
              <ul className="mt-4 space-y-2 text-sm text-slate-300 flex-1">
                {t.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2"><Check className="w-4 h-4 text-cyan-400 mt-0.5 shrink-0" /> <span>{b}</span></li>
                ))}
              </ul>
              <Link to={`/book?service=${t.id}`} data-testid={`book-${t.id}`}
                className="dg-btn-primary rounded-full px-5 py-2.5 text-sm mt-6 flex items-center justify-center gap-2">
                Book this <ArrowRight className="w-4 h-4" />
              </Link>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mb-6">
        <span className="dg-eyebrow">What I can help with</span>
        <h2 className="mt-2 text-lg md:text-xl font-bold font-display">Any gadget, any brand.</h2>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        {AREAS.map((a, i) => {
          const Icon = a.icon;
          return (
            <div key={a.title} className={`dg-card rounded-xl p-5 flex gap-4 dg-reveal dg-reveal-${(i % 4) + 1}`}>
              <div className="w-10 h-10 rounded-lg bg-cyan-500/10 border border-cyan-500/30 grid place-items-center shrink-0"><Icon className="w-5 h-5 text-cyan-400" /></div>
              <div>
                <div className="font-semibold">{a.title}</div>
                <p className="text-slate-400 text-sm mt-1">{a.desc}</p>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-8 flex flex-wrap items-center gap-6"><Link to="/ask" className="dg-link text-sm">Not sure which session? Ask Dr.Gadget — it's free →</Link><Link to="/gift" className="dg-link text-sm" data-testid="services-gift-link">Give a session as a gift →</Link></div>
      <div className="mt-12 -mx-4 sm:-mx-6"><InstallBanner /></div>
      <div className="mt-4"><PrepChecklist /></div>
    </div>
  );
}
